import { __ } from '@wordpress/i18n'
import { Calendar } from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import deLocale from '@fullcalendar/core/locales/de'
import enLocale from '@fullcalendar/core/locales/en-gb'
import Choices from 'choices.js'
import 'choices.js/public/assets/styles/choices.min.css'

const isMobileViewport = () => window.innerWidth <= 768

const debounce = (fn, delay) => {
  let timeoutId
  return (...args) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => {
      fn(...args)
    }, delay)
  }
}

const initVeranstaltungskalender = () => {
  const blocks = document.querySelectorAll('.veranstaltungskalender-block')

  blocks.forEach(block => {
    // Prevent double initialization
    if (block.dataset.initialized === 'true') {
      return
    }
    block.dataset.initialized = 'true'

    const defaultView = block.dataset.defaultView || 'calendar'
    const showFilters = block.dataset.showFilters === 'true'
    const eventsPerPage = parseInt(block.dataset.eventsPerPage) || 10
    let mode = isMobileViewport() ? 'mobile' : 'desktop'

    const setupBlock = currentMode => {
      const container = document.createElement('div')
      container.className = 'veranstaltungskalender-container'

      let filterForm = null
      if (showFilters) {
        filterForm = createFilterForm()
        container.appendChild(filterForm)
        initChoices(filterForm)
      }

      let tabsContainer = null
      const effectiveDefaultView =
        currentMode === 'mobile' ? 'list' : defaultView || 'calendar'

      if (currentMode === 'desktop') {
        tabsContainer = createTabs(effectiveDefaultView)
        container.appendChild(tabsContainer)
      }

      const viewsContainer = createViewsContainer(
        effectiveDefaultView,
        currentMode === 'mobile'
      )
      container.appendChild(viewsContainer)

      if (filterForm) {
        const loadingEl = document.createElement('div')
        loadingEl.className = 'veranstaltungskalender-loading'
        loadingEl.innerHTML =
          '<div class="veranstaltungskalender-loading__spinner"></div>'
        loadingEl.style.display = 'none'
        filterForm.appendChild(loadingEl)
      }

      block.innerHTML = ''
      block.appendChild(container)

      const calendarEl =
        currentMode === 'desktop'
          ? viewsContainer.querySelector('#calendar-view')
          : null
      let calendar = null

      if (currentMode === 'desktop' && effectiveDefaultView === 'calendar') {
        calendar = initCalendar(calendarEl)
      }

      if (currentMode === 'desktop' && tabsContainer) {
        setupTabSwitching(tabsContainer, viewsContainer, calendarEl, calendar)
      }

      setupFilterSubmit(
        container,
        calendarEl,
        calendar,
        viewsContainer,
        eventsPerPage
      )

      loadInitialEvents(calendarEl, calendar, viewsContainer, eventsPerPage)
    }

    setupBlock(mode)

    const handleResize = debounce(() => {
      const nextMode = isMobileViewport() ? 'mobile' : 'desktop'
      if (nextMode === mode) return
      mode = nextMode
      setupBlock(mode)
    }, 200)

    window.addEventListener('resize', handleResize)
  })
}

const initChoices = () => {
  // No multi-select filters at the moment.
}

const createFilterForm = () => {
  const form = document.createElement('form')
  form.className = 'veranstaltungen-filter'
  form.innerHTML = `
    <div class="filter-row">
      <div class="filter-field">
        <label for="search-input">${__('Search', 'nxt_event_calendar')}</label>
        <input type="text" id="search-input" name="s" placeholder="${__(
          'Search events…',
          'nxt_event_calendar'
        )}" />
      </div>
    </div>

    <div class="filter-row">
      <div class="filter-field">
        <label for="date-from">${__('From', 'nxt_event_calendar')}</label>
        <input type="date" id="date-from" name="date_from" />
      </div>

      <div class="filter-field">
        <label for="date-to">${__('To', 'nxt_event_calendar')}</label>
        <input type="date" id="date-to" name="date_to" />
      </div>
    </div>

    <div class="filter-actions">
      <button type="submit" class="filter-submit">${__(
        'Apply filter',
        'nxt_event_calendar'
      )}</button>
      <button type="reset" class="filter-reset">${__(
        'Reset',
        'nxt_event_calendar'
      )}</button>
    </div>
  `

  return form
}

const createTabs = defaultView => {
  const tabs = document.createElement('div')
  tabs.className = 'veranstaltungen-tabs'
  tabs.innerHTML = `
    <button class="tab-btn ${
      defaultView === 'calendar' ? 'active' : ''
    }" data-view="calendar">
      <span class="dashicons dashicons-calendar-alt"></span> ${__(
        'Calendar',
        'nxt_event_calendar'
      )}
    </button>
    <button class="tab-btn ${
      defaultView === 'list' ? 'active' : ''
    }" data-view="list">
      <span class="dashicons dashicons-list-view"></span> ${__(
        'List',
        'nxt_event_calendar'
      )}
    </button>
  `

  return tabs
}

const createViewsContainer = (defaultView, listOnly = false) => {
  const container = document.createElement('div')
  container.className = 'veranstaltungen-views'
  if (listOnly) {
    container.innerHTML = `
      <div id="list-view" class="view-content active">
        <div class="veranstaltungen-list"></div>
      </div>
    `
  } else {
    container.innerHTML = `
      <div id="calendar-view" class="view-content ${
        defaultView === 'calendar' ? 'active' : 'hidden'
      }"></div>
      <div id="list-view" class="view-content ${
        defaultView === 'list' ? 'active' : 'hidden'
      }">
        <div class="veranstaltungen-list"></div>
      </div>
    `
  }

  return container
}

const initCalendar = calendarEl => {
  const lang = (window.nxt_event_calendar?.locale || 'de').toLowerCase()
  const locale = lang === 'en' ? enLocale : deLocale

  const calendar = new Calendar(calendarEl, {
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    initialView: 'dayGridMonth',
    locale,
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth,timeGridWeek,timeGridDay'
    },
    buttonText: {
      today: __('Today', 'nxt_event_calendar'),
      month: __('Month', 'nxt_event_calendar'),
      week: __('Week', 'nxt_event_calendar'),
      day: __('Day', 'nxt_event_calendar')
    },
    eventTimeFormat: {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    },
    slotLabelFormat: {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    },
    events: [],
    eventClick: info => {
      if (info.event.url) {
        info.jsEvent.preventDefault()
        window.location.href = info.event.url
      }
    },
    height: 'auto'
  })

  calendar.render()
  return calendar
}

const setupTabSwitching = (
  tabsContainer,
  viewsContainer,
  calendarEl,
  calendarInstance
) => {
  const tabButtons = tabsContainer.querySelectorAll('.tab-btn')

  tabButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      const targetView = btn.dataset.view

      tabButtons.forEach(b => b.classList.remove('active'))
      btn.classList.add('active')

      const calendarView = viewsContainer.querySelector('#calendar-view')
      const listView = viewsContainer.querySelector('#list-view')

      if (targetView === 'calendar') {
        calendarView.classList.remove('hidden')
        calendarView.classList.add('active')
        listView.classList.remove('active')
        listView.classList.add('hidden')

        if (!calendarInstance) {
          calendarInstance = initCalendar(calendarEl)
          const events = window.currentVeranstaltungen || []
          calendarInstance.removeAllEvents()
          calendarInstance.addEventSource(events)
        } else {
          setTimeout(() => {
            calendarInstance.updateSize()
          }, 100)
        }
      } else {
        listView.classList.remove('hidden')
        listView.classList.add('active')
        calendarView.classList.remove('active')
        calendarView.classList.add('hidden')
      }
    })
  })
}

const setupFilterSubmit = (
  container,
  calendarEl,
  calendarInstance,
  viewsContainer,
  eventsPerPage
) => {
  const form = container.querySelector('.veranstaltungen-filter')
  if (!form) return
  const loadingEl = form.querySelector('.veranstaltungskalender-loading')
  const submitButton = form.querySelector('.filter-submit')
  const resetButton = form.querySelector('.filter-reset')

  form.addEventListener('submit', e => {
    e.preventDefault()

    const formData = new FormData(form)
    formData.append('action', 'nxt_veranstaltungen_search')
    formData.append('nonce', nxt_event_calendar.nonce)

    form.classList.add('is-loading')
    if (loadingEl) loadingEl.style.display = 'flex'
    if (submitButton) submitButton.disabled = true
    if (resetButton) resetButton.disabled = true

    fetch(nxt_event_calendar.ajaxurl, {
      method: 'POST',
      body: formData
    })
      .then(response => response.json())
      .then(data => {
        form.classList.remove('is-loading')
        if (loadingEl) loadingEl.style.display = 'none'
        if (submitButton) submitButton.disabled = false
        if (resetButton) resetButton.disabled = false

        if (data.success) {
          window.currentVeranstaltungen = data.data.calendar

          if (calendarInstance) {
            calendarInstance.removeAllEvents()
            calendarInstance.addEventSource(data.data.calendar)
          }

          renderListView(data.data.list, viewsContainer, eventsPerPage)
        }
      })
      .catch(error => {
        console.error('Error fetching veranstaltungen:', error)
        form.classList.remove('is-loading')
        if (loadingEl) loadingEl.style.display = 'none'
        if (submitButton) submitButton.disabled = false
        if (resetButton) resetButton.disabled = false
      })
  })

  form.addEventListener('reset', () => {
    setTimeout(() => {
      loadInitialEvents(
        calendarEl,
        calendarInstance,
        viewsContainer,
        eventsPerPage
      )
    }, 100)
  })
}

const loadInitialEvents = (
  calendarEl,
  calendarInstance,
  viewsContainer,
  eventsPerPage
) => {
  const veranstaltungen = nxt_event_calendar.veranstaltungen || []
  const calendarEvents = veranstaltungen.map(v => convertToCalendarFormat(v))

  window.currentVeranstaltungen = calendarEvents

  if (calendarInstance) {
    calendarInstance.removeAllEvents()
    calendarInstance.addEventSource(calendarEvents)
  }

  renderListView(veranstaltungen, viewsContainer, eventsPerPage)
}

const convertToCalendarFormat = veranstaltung => {
  const event = {
    id: veranstaltung.id,
    title: veranstaltung.title,
    url: veranstaltung.permalink
  }

  if (veranstaltung.startdatum) {
    let start = veranstaltung.startdatum
    if (veranstaltung.startzeit && !veranstaltung.ganztaegig) {
      start += 'T' + veranstaltung.startzeit
    } else {
      event.allDay = true
    }
    event.start = start
  }

  if (veranstaltung.enddatum) {
    let end = veranstaltung.enddatum
    if (veranstaltung.endzeit && !veranstaltung.ganztaegig) {
      end += 'T' + veranstaltung.endzeit
    }
    event.end = end
  }

  event.extendedProps = {
    ort: veranstaltung.ort,
    ganztaegig: veranstaltung.ganztaegig ?? false
  }

  return event
}

const renderListView = (veranstaltungen, viewsContainer, eventsPerPage) => {
  const listContainer = viewsContainer.querySelector('.veranstaltungen-list')

  if (veranstaltungen.length === 0) {
    listContainer.innerHTML = `<p class="no-results">${__(
      'No events found.',
      'nxt_event_calendar'
    )}</p>`
    return
  }

  let html = ''

  veranstaltungen.forEach(veranstaltung => {
    const dateStr = formatDateRange(
      veranstaltung.startdatum,
      veranstaltung.enddatum,
      veranstaltung.startzeit,
      veranstaltung.endzeit,
      veranstaltung.ganztaegig
    )

    const location = veranstaltung.ort ?? ''
    const hasMeta = dateStr || location

    html += `
      <article class="veranstaltung-item">
        ${
          veranstaltung.thumbnail
      ? `
        <div class="veranstaltung-image-container">
          <a href="${veranstaltung.permalink}">
            <div class="veranstaltung-image">
              <img src="${veranstaltung.thumbnail}" alt="${veranstaltung.title}" />
            </div>
          </a>
        </div>
        `
            : ''
        }
        <div class="veranstaltung-content">
        <div class="veranstaltung-header">
          <h3><a href="${veranstaltung.permalink}">${
      veranstaltung.title
    }</a></h3>
        </div>
        ${
          hasMeta
            ? `
          <div class="veranstaltung-meta">
            ${
              dateStr
                ? `
              <span class="meta-item meta-date">
                <span class="dashicons dashicons-calendar-alt"></span> ${dateStr}
              </span>
            `
                : ''
            }
            ${
              location
                ? `
              <span class="meta-item meta-location">
                <span class="dashicons dashicons-location"></span> ${location}
              </span>
            `
                : ''
            }
          </div>
        `
            : ''
        }
        ${
          veranstaltung.content
            ? `<div class="veranstaltung-excerpt">${veranstaltung.content}</div>`
            : ''
        }
        </div>
      </article>
    `
  })

  listContainer.innerHTML = html
}

const formatDateRange = (startDate, endDate, startTime, endTime, allDay) => {
  if (!startDate) return ''

  const formatDate = dateStr => {
    const [year, month, day] = dateStr.split('-')
    return `${day}.${month}.${year}`
  }

  const formatTime = timeStr => {
    if (!timeStr) return ''
    return timeStr.substring(0, 5)
  }

  let result = formatDate(startDate)

  if (startTime && !allDay) {
    result += ` ${formatTime(startTime)}`
  }

  if (endDate && endDate !== startDate) {
    result += ` - ${formatDate(endDate)}`
    if (endTime && !allDay) {
      result += ` ${formatTime(endTime)}`
    }
  } else if (endTime && !allDay && endTime !== startTime) {
    result += ` - ${formatTime(endTime)}`
  }

  return result
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVeranstaltungskalender)
} else {
  initVeranstaltungskalender()
}
